import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { ScreenSize } from '@jbhi-fi/lanyard-ui/themes/breakpoints';
import { breakpointSizePx } from '@jbhi-fi/lanyard-ui/themes';
const breakpointKeys = Object.keys(breakpointSizePx).reverse() as Array<ScreenSize>;

type CurrentBreakpoint = {
  name: ScreenSize;
  value: number;
};

const getCurrentBreakpoint = (screenWidth: number): CurrentBreakpoint => {
  const currentBreakpoint = breakpointKeys.find(key => screenWidth >= breakpointSizePx[key]) ?? 'xxl';
  return {
    name: currentBreakpoint,
    value: breakpointSizePx[currentBreakpoint],
  };
};

const CurrentBreakpointContext = createContext<CurrentBreakpoint | undefined>(undefined);

export const CurrentBreakpointProvider = ({ children }: { children: ReactNode }) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<CurrentBreakpoint>(() => {
    // Default to 'xs' for server-side rendering.
    if (typeof window !== 'undefined') {
      return getCurrentBreakpoint(window.innerWidth);
    }
    return { value: 0, name: 'xs' };
  });

  useEffect(() => {
    const handleResize = () => {
      const newBreakpoint = getCurrentBreakpoint(window.innerWidth);
      setCurrentBreakpoint(prevBreakpoint =>
        prevBreakpoint.value !== newBreakpoint.value ? newBreakpoint : prevBreakpoint
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return <CurrentBreakpointContext.Provider value={currentBreakpoint}>{children}</CurrentBreakpointContext.Provider>;
};

export const useCurrentBreakpoint = (): CurrentBreakpoint => {
  const context = useContext(CurrentBreakpointContext);
  if (context === undefined) {
    throw new Error('useCurrentBreakpoint must be used within a CurrentBreakpointProvider');
  }

  return context;
};
